/**
 * Where a fragment the corrector (or the game's attempt check) quoted sits in
 * the learner's turn: the ONE matcher the bubble's underline (speak
 * `messages/annotate.tsx`) and the game stamp's sentence filter
 * (`gameJudgedCorrections`, `game-round.ts`) share, so the stamp reads a chip
 * where the learner sees it underlined.
 *
 * The LLM does not always copy the fragment verbatim, so the match goes from
 * strict to loose:
 *
 *   1. exact substring (the usual case);
 *   2. case-insensitive (same length, so the offsets stay valid);
 *   3. the same words in a row, ignoring case and the punctuation around each
 *      word ("We no eat meat." in "we no eat meat, right?");
 *   4. fuzzy, by tokens (positional alignment), as a last resort —
 *      conservative (≥ 3 words and ≥ 70 % of them equal) so as not to
 *      underline the wrong span ("a new house" quoted for "my new house").
 *
 * `null` when nothing matches with confidence (the fragment is not
 * underlined). Pure, dependency-free.
 */

/** A `[start, end)` character range of the text. */
export interface TextSpan {
  start: number;
  end: number;
}

const FUZZY_MIN_TOKENS = 3;
const FUZZY_MIN_RATIO = 0.7;

interface Token {
  lo: number;
  hi: number;
  /** Lower-cased, punctuation around the word stripped (may be empty: a lone "—"). */
  w: string;
}

/** Lower-case and strip what surrounds a word (keeps inner apostrophes / hyphens). */
function bareWord(raw: string): string {
  return raw.toLowerCase().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
}

function tokensFrom(text: string, from: number): Token[] {
  const out: Token[] = [];
  const re = /\S+/g;
  re.lastIndex = from;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    out.push({ lo: m.index, hi: m.index + m[0].length, w: bareWord(m[0]) });
  }
  return out;
}

/** Tier 3: the fragment's words, in a row, case and punctuation aside. */
function wordRunSpan(text: string, fragment: string, from: number): TextSpan | null {
  const want = (fragment.match(/\S+/g) ?? []).map(bareWord).filter((w) => w.length > 0);
  if (want.length === 0) return null;
  const toks = tokensFrom(text, from).filter((t) => t.w.length > 0);
  for (let i = 0; i + want.length <= toks.length; i += 1) {
    let ok = true;
    for (let j = 0; j < want.length; j += 1) {
      if (toks[i + j]!.w !== want[j]) {
        ok = false;
        break;
      }
    }
    if (ok) return { start: toks[i]!.lo, end: toks[i + want.length - 1]!.hi };
  }
  return null;
}

/**
 * Tier 4: the contiguous window of words of `text` (from `from`) that best
 * aligns, position by position, with the words of `fragment`. Only fragments
 * of ≥ 3 words with ≥ 70 % equal words. Covers the typical word the LLM
 * swapped or normalized ("a" → "my") in an otherwise identical phrase.
 */
function fuzzySpan(text: string, fragment: string, from: number): TextSpan | null {
  const want = fragment.toLowerCase().match(/\S+/g) ?? [];
  if (want.length < FUZZY_MIN_TOKENS) return null;
  const toks = tokensFrom(text, from).map((t) => ({
    ...t,
    w: text.slice(t.lo, t.hi).toLowerCase(),
  }));
  const L = want.length;
  const minMatches = Math.ceil(L * FUZZY_MIN_RATIO);
  let best: (TextSpan & { score: number }) | null = null;
  for (let i = 0; i + L <= toks.length; i += 1) {
    let score = 0;
    for (let j = 0; j < L; j += 1) if (toks[i + j]!.w === want[j]) score += 1;
    if (score >= minMatches && (!best || score > best.score)) {
      best = { start: toks[i]!.lo, end: toks[i + L - 1]!.hi, score };
    }
  }
  return best ? { start: best.start, end: best.end } : null;
}

/** The span of `fragment` in `text`, searching from `from` (see the module doc). */
export function locateTextSpan(text: string, fragment: string, from = 0): TextSpan | null {
  if (fragment.trim() === '') return null;
  const exact = text.indexOf(fragment, from);
  if (exact >= 0) return { start: exact, end: exact + fragment.length };
  const ci = text.toLowerCase().indexOf(fragment.toLowerCase(), from);
  if (ci >= 0) return { start: ci, end: ci + fragment.length };
  return wordRunSpan(text, fragment, from) ?? fuzzySpan(text, fragment, from);
}

/**
 * The span of each fragment, in order, the way the bubble underlines a
 * turn's corrections: each one is searched on its own (a wide phrase does not
 * hide the narrow ones nested in it), and a fragment REPEATED in the list is
 * searched after the previous occurrence's end — two chips on the same words
 * land on two different places. `null` = not found.
 */
export function locateTextSpans(
  text: string,
  fragments: ReadonlyArray<string>,
): Array<TextSpan | null> {
  const lastEndByFragment = new Map<string, number>();
  return fragments.map((fragment) => {
    const span = locateTextSpan(text, fragment, lastEndByFragment.get(fragment) ?? 0);
    if (span) lastEndByFragment.set(fragment, span.end);
    return span;
  });
}
